<template>
  <div class="forum-wrapper">
    <v-container fluid class="forum-dashboard-container">
      <v-row justify="center">
        <v-col cols="12" sm="8" md="6">
          <v-card outlined class="forum-card">
            <v-card-title class="text-h5">Edit Post</v-card-title>
            <v-card-text>
              <!-- Removed fixed height; added "editor-area" -->
              <div :key="editEditorKey" ref="quillEdit" class="quill-editor editor-area"></div>
            </v-card-text>
            <v-card-actions>
              <v-btn color="primary" @click="savePost">Save</v-btn>
              <v-btn variant="text" @click="cancelEdit">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import api from "@/plugins/axios";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "quill-mention/dist/quill.mention.css";
import quillImageUploadMixin from "@/mixins/quillImageUploadMixin";
import { imageHandler } from "@/utils/quillImageUploader";
import { sanitizeContent } from "@/utils/sanitizeHelper";

export default {
  name: "ForumPostEdit",
  mixins: [quillImageUploadMixin],
  data() {
    return {
      post: null,
      quill: null,
      editEditorKey: 0,
    };
  },
  methods: {
    async fetchPost() {
      try {
        const postId = this.$route.params.postId;
        const res = await api.get(`/api/forum-posts/${postId}/`);
        this.post = res.data;
      } catch (err) {
        console.error("Error fetching post:", err);
      }
    },
    initQuill() {
      const modulesObj = {
        toolbar: {
          container: [
            [{ font: [] }],
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ align: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            ["blockquote", "code-block"],
            ["link", "image", "video"],
            ["clean"],
          ],
          handlers: {
            image: function() { imageHandler(this.quill); },
          },
        },
        mention: {
          mentionDenotationChars: ["@"],
          source: (searchTerm, renderList) => {
            if (!searchTerm) {
              renderList([], searchTerm);
              return;
            }
            api.get("/api/forum/mention-suggestions/", { params: { query: searchTerm } })
              .then((resp) => {
                renderList(resp.data, searchTerm);
              })
              .catch((err) => {
                console.error("Mention fetch error:", err);
                renderList([], searchTerm);
              });
          },
        },
      };
      if (!this.$refs.quillEdit) {
        console.error("Ref quillEdit not found");
        return;
      }
      this.quill = new Quill(this.$refs.quillEdit, {
        theme: "snow",
        modules: modulesObj,
      });
    },
    loadPostIntoQuill() {
      if (this.post && this.quill) {
        // Start from server HTML
        let html = this.post.content || "";

        // Repair any `/api/images/serve?...` back to raw `/media/...` so Quill shows actual images immediately
        try {
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const imgs = Array.from(tmp.querySelectorAll('img[src]'));
          imgs.forEach((img) => {
            const raw = img.getAttribute('src') || '';
            try {
              const u = new URL(raw, window.location.origin);
              if (u.pathname.startsWith('/api/images/serve')) {
                const srcParam = u.searchParams.get('src');
                if (srcParam) {
                  img.setAttribute('src', srcParam);
                }
              }
            } catch (_) {
              // ignore URL parse errors
            }
          });
          html = tmp.innerHTML;
        } catch (_) {
          // if anything goes wrong, fall back to the original html
        }

        this.quill.root.innerHTML = html;
      }
    },
    async savePost() {
      if (!this.quill) return;
      const content = this.quill.root.innerHTML;
      if (!content || content === "<p><br></p>") return;

      // Sanitize & normalize to use `/api/images/serve` for local media
      const clean = sanitizeContent(content);

      try {
        await api.put(`/api/forum-posts/${this.$route.params.postId}/`, { content: clean });
        this.$router.push(`/forum/${this.post.thread}`);
      } catch (err) {
        console.error("Error saving post:", err);
      }
    },
    cancelEdit() {
      if (this.post) {
        this.$router.push(`/forum/${this.post.thread}`);
      } else {
        this.$router.push("/forum");
      }
    },
  },
  async created() {
    await this.fetchPost();
  },
  mounted() {
    this.initQuill();
    this.$nextTick(() => {
      this.loadPostIntoQuill();
    });
    setTimeout(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    }, 200);
  },
};
</script>

<style scoped>
.forum-wrapper {
  overflow: hidden;
}

.forum-dashboard-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  padding: 20px;
  min-height: 100vh;
  position: relative;
}

.forum-card {
  background-color: #fff;
  border-radius: 8px;
}

/* Make the Quill editor bigger, like in handbook */
.quill-editor.editor-area {
  min-height: 300px;
  background-color: #fff;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

/* Constrain images within Quill editor content */
:deep(.quill-editor .ql-editor img) {
  max-width: 100% !important;
  height: auto !important;
  max-height: 250px !important;
}
</style>
